import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="confirmation-dialog"
export default class extends Controller {
  static targets = ["modal", "title", "message", "confirmButton", "cancelButton"]

  connect() {
    // Only hide modal if this controller has modal target (is the actual modal)
    if (this.hasModalTarget) {
      this.hideModal()
    }
  }

  // Show confirmation dialog
  show(event) {
    event.preventDefault()

    const link = event.currentTarget
    const title = link.dataset.confirmTitle || "Confirm Action"
    const message = link.dataset.confirmMessage || "Are you sure you want to continue?"
    const confirmText = link.dataset.confirmText || "Confirm"
    const cancelText = link.dataset.cancelText || "Cancel"
    const confirmClass = link.dataset.confirmClass || "bg-red-600 hover:bg-red-700"

    // If this controller doesn't have modal targets, find the global modal controller
    if (!this.hasModalTarget) {
      const globalModal = document.querySelector('[data-controller*="confirmation-dialog"][data-confirmation-dialog-target="modal"]')
      if (globalModal) {
        const globalController = this.application.getControllerForElementAndIdentifier(globalModal, 'confirmation-dialog')
        if (globalController) {
          globalController.show(event)
          return
        }
      }
    }

    // Store the original link for later action
    this.originalLink = link

    // Update modal content
    this.titleTarget.textContent = title
    this.messageTarget.textContent = message
    this.confirmButtonTarget.textContent = confirmText
    this.cancelButtonTarget.textContent = cancelText

    // Update confirm button styling
    this.confirmButtonTarget.className = `inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 ${confirmClass}`

    // Show modal
    this.showModal()
  }

  // Confirm action - proceed with original link action
  confirm() {
    if (this.originalLink) {
      // For delete operations, use Turbo's built-in method handling
      if (this.originalLink.dataset.turboMethod === "delete") {
        // Import Turbo visit method
        import("@hotwired/turbo").then(({ Turbo }) => {
          // Use Turbo to handle the DELETE request properly
          Turbo.visit(this.originalLink.href, {
            action: "replace",
            requestOptions: {
              method: "DELETE",
              headers: {
                "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
              }
            }
          })
        })
      } else {
        // For regular links, use Turbo navigation
        import("@hotwired/turbo").then(({ Turbo }) => {
          Turbo.visit(this.originalLink.href)
        })
      }
    }

    if (this.hasModalTarget) {
      this.hideModal()
    }
  }

  // Cancel action
  cancel() {
    if (this.hasModalTarget) {
      this.hideModal()
    }
  }

  // Hide modal when clicking outside
  clickOutside(event) {
    if (this.hasModalTarget && event.target === this.modalTarget) {
      this.hideModal()
    }
  }

  // Handle escape key
  keydown(event) {
    if (this.hasModalTarget && event.key === "Escape") {
      this.hideModal()
    }
  }

  // Handle clicks on confirmation trigger elements
  handleTriggerClick(event) {
    if (event.target.matches('.confirmation-trigger') || event.target.closest('.confirmation-trigger')) {
      const trigger = event.target.matches('.confirmation-trigger') ? event.target : event.target.closest('.confirmation-trigger')

      // Create a synthetic event for the show method
      const syntheticEvent = {
        preventDefault: () => event.preventDefault(),
        currentTarget: trigger
      }

      this.show(syntheticEvent)
    }
  }

  // Private methods
  showModal() {
    this.modalTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")

    // Add event listeners
    document.addEventListener("keydown", this.keydown.bind(this))

    // Focus the cancel button by default
    this.cancelButtonTarget.focus()
  }

  hideModal() {
    this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")

    // Remove event listeners
    document.removeEventListener("keydown", this.keydown.bind(this))

    // Clear original link reference
    this.originalLink = null
  }
}